import type { Deps } from '../interfaces/stores';
import type { Ballot, FlowCell, FlowRow, Round, RoundNames, Settings, TimerRecord, Tournament } from '../schemas/entities';

export const DEFAULT_SETTINGS: Settings = {
  id: 'settings',
  currentTournamentId: null,
  timerAlertsSec: [0],
  timerVolume: 0.8,
  purgeNamesAfterHours: 72,
  flowFontScale: 1,
  flowLayout: 'single',
  uiFont: 'system',
  writingFont: 'inherit',
};

/** In-memory implementation of every store. Used by tests; handy for demos. */
export const createMemoryDeps = (opts: { now?: () => number } = {}): Deps => {
  const tournaments = new Map<string, Tournament>();
  const rounds = new Map<string, Round>();
  const names = new Map<string, RoundNames>();
  const rows = new Map<string, FlowRow>();
  const cells = new Map<string, FlowCell>();
  const timers = new Map<string, TimerRecord>();
  const ballots = new Map<string, Ballot>();
  let settings = { ...DEFAULT_SETTINGS };
  let seq = 0;
  const clone = <T>(x: T): T => (x === undefined ? x : JSON.parse(JSON.stringify(x)));

  return {
    tournaments: {
      get: async (id) => clone(tournaments.get(id)),
      list: async () => [...tournaments.values()].map(clone),
      put: async (t) => void tournaments.set(t.id, clone(t)),
      delete: async (id) => void tournaments.delete(id),
    },
    rounds: {
      get: async (id) => clone(rounds.get(id)),
      listByTournament: async (tid) => [...rounds.values()].filter((r) => r.tournamentId === tid).map(clone),
      put: async (r) => void rounds.set(r.id, clone(r)),
      delete: async (id) => void rounds.delete(id),
    },
    names: {
      get: async (id) => clone(names.get(id)),
      put: async (n) => void names.set(n.roundId, clone(n)),
      delete: async (id) => void names.delete(id),
      listDueForPurge: async (now) => [...names.values()].filter((n) => n.purgeAfter !== null && n.purgeAfter <= now).map(clone),
    },
    flow: {
      rows: async (rid) => [...rows.values()].filter((r) => r.roundId === rid).map(clone),
      cells: async (rid) => [...cells.values()].filter((c) => c.roundId === rid).map(clone),
      getCell: async (id) => clone(cells.get(id)),
      putRow: async (r) => void rows.set(r.id, clone(r)),
      putCell: async (c) => void cells.set(c.id, clone(c)),
      deleteRow: async (rowId) => {
        rows.delete(rowId);
        for (const c of [...cells.values()]) if (c.rowId === rowId) cells.delete(c.id);
      },
      deleteRound: async (rid) => {
        for (const r of [...rows.values()]) if (r.roundId === rid) rows.delete(r.id);
        for (const c of [...cells.values()]) if (c.roundId === rid) cells.delete(c.id);
      },
    },
    timers: {
      get: async (id) => clone(timers.get(id)),
      put: async (t) => void timers.set(t.roundId, clone(t)),
      delete: async (id) => void timers.delete(id),
    },
    ballots: {
      get: async (id) => clone(ballots.get(id)),
      put: async (b) => void ballots.set(b.roundId, clone(b)),
      delete: async (id) => void ballots.delete(id),
    },
    settings: {
      get: async () => clone(settings),
      put: async (s) => void (settings = clone(s)),
    },
    backup: {
      exportAll: async () => ({
        tournaments: [...tournaments.values()],
        rounds: [...rounds.values()],
        names: [...names.values()],
        rows: [...rows.values()],
        cells: [...cells.values()],
        timers: [...timers.values()],
        ballots: [...ballots.values()],
      }),
      importAll: async (b) => {
        b.tournaments.forEach((x) => tournaments.set(x.id, x));
        b.rounds.forEach((x) => rounds.set(x.id, x));
        b.names.forEach((x) => names.set(x.roundId, x));
        b.rows.forEach((x) => rows.set(x.id, x));
        b.cells.forEach((x) => cells.set(x.id, x));
        b.timers.forEach((x) => timers.set(x.roundId, x));
        b.ballots.forEach((x) => ballots.set(x.roundId, x));
      },
    },
    clock: { now: opts.now ?? (() => Date.now()) },
    ids: { next: () => `id${++seq}` },
    tx: { run: (fn) => fn() },
  };
};
